import { useNavigate, useLocation, useParams, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Title } from "../../types/Title";
import useTitlesService from "../../services/TitlesService";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import BackButton from "../../components/BackButton";
import TitleDetailForm from "../../components/Title/TitleDetailForm";
import useSessionsService from "../../services/SessionsService";
import useUserService from "../../services/UserService";
import { SessionSummary } from "../../types/Session";
import { User } from "../../types/User";
import useExperienceService from "../../services/ExperienceService";
import { Experience } from "../../types/Experience";

export default function AdminSessionsDetailPage() {
  const navigate = useNavigate();
  const { sessionId, userId } = useParams();
  const { getSession } = useSessionsService();
  const {getExperienceById} = useExperienceService();
  const { getUser } = useUserService();

  const [isLoading, setIsLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [experience, setExperience] = useState<Experience | null>(null);

  useEffect(() => {
    if (initiated) {
      return; // Prevent re-fetching if already initiated
    }

    if (!initiated) {
      setIsLoading(true);
      setInitiated(true);
    }

    getSession(sessionId as string, userId as string)
      .then((session) => {
        setSession(session);
      })
      .catch((error) => {
        console.error("Error fetching title:", error);
      });
  }, [initiated]);

  useEffect(() => {
    if (session) {
      setIsLoading(false);
    }

    if (session && session.experienceId) {
      getExperienceById(session.experienceId)
        .then((experience) => {
          setExperience(experience);
        })
        .catch((error) => {
          console.error("Error fetching experience:", error);
        });
    }

    if (session && session.userId) {
      getUser(session.userId)
        .then((user) => {
          setUser(user);
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
        });
    }
  }, [session]);

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <div className="flex flex-row">
        <BackButton />
        <PageTitle className="text-4xl">
          {session ? `Session details` : `Loading`}{" "}
          {!session && <LoadingEllipsis />}
        </PageTitle>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
          {session ? (
            <>
              <h2 className="text-2xl">Core information</h2>
              <div className="card p-4">
                <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                  <dt>Id:</dt>
                  <dd>{session.id}</dd>
                  <dt>Started:</dt>
                  <dd>{new Date(session.started).toLocaleString("en-GB")}</dd>
                  <dt>Session type</dt>
                  <dd>{session.type}</dd>
                  <dt>Ended:</dt>
                  <dd>
                    {session.ended
                      ? new Date(session.ended).toLocaleString("en-GB")
                      : "Unknown"}
                  </dd>
                  <dt>IP Address:</dt>
                  <dd>{session.publicAddress ?? "Unknown"}</dd>
                  </dl>
              </div>
              <h2 className="text-2xl">Device information</h2>
              <div className="card p-4">
                <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                  <dt>Device type:</dt>
                  <dd>{session.device ?? "Unknown"}</dd>
                  <dt>Device name:</dt>
                  <dd>{session.deviceName ?? "Unknown"}</dd>
                  <dt>Platform:</dt>
                  <dd>{session.platform ?? "Unknown"}</dd>
                  <dt>Audio type:</dt>
                  <dd>{session.audioType ?? "Unknown"}</dd>
                  <dt>Mic type:</dt>
                  <dd>{session.micType ?? "Unknown"}</dd>
                  <dt>Stream Camera Type:</dt>
                  <dd>{session.streamCameraType ?? "Unknown"}</dd>
                  <dt>Graphices API:</dt>
                  <dd>{session.graphicsApi ?? "Unknown"}</dd>
                  <dt>SDK Version:</dt>
                  <dd>{session.sdkVersion ?? "Unknown"}</dd>
                  <dt>Engine Version:</dt>
                  <dd>{session.engineVersion ?? "Unknown"}</dd>
                  <dt>Target resolution:</dt>
                  <dd>{session.targetResolution ?? "Unknown"}</dd>
                  <dt>Target framerate:</dt>
                  <dd>{session.targetFramerate ?? "Unknown"}</dd>
                  <dt>Target bitrate:</dt>
                  <dd>{session.targetBitrate ?? "Unknown"}</dd>
                  <dt>XR Rendering?:</dt>
                  <dd>{session.isXRRendering ?? "Unknown"}</dd>
                </dl>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl">Core information</h2>
              <Ghost additionalClasses="h-24 col-span-2 rounded" />
              <h2 className="text-2xl">Device information</h2>
              <Ghost additionalClasses="h-24 col-span-2 rounded" />
            </>
          )}
        </div>
        <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
          <h2 className="text-2xl">User</h2>
          {user ? (
            <>
              <div className="card p-4">
                <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                  <dt>Id:</dt>
                  <dd>
                  <NavLink
                    to={`/admin/users/${user.id}`}
                    className="underline"
                  >
                    {user.id}
                  </NavLink>
                  </dd>

                  <dt>Email:</dt>
                  <dd>{user.email}</dd>

                  <dt>Given name:</dt>
                  <dd>{user.givenName}</dd>

                  <dt>Family name:</dt>
                  <dd>{user.familyName}</dd>

                  <dt>Nickname</dt>
                  <dd>{user.nickname}</dd>

                  <dt>Picture</dt>
                  <dd>
                    {user.picture ? (
                      <img src={user.picture} alt="User profile" className="w-24 h-24 rounded-full" />
                    ) : (
                      <span className="text-gray-500">No picture</span>
                    )}
                    </dd>

                  </dl>
                </div>
            </>
          ) : (
            <Ghost additionalClasses="h-24 col-span-2 rounded" />
          )}
          <h2 className="text-2xl">Experience</h2>
          {experience ? (
            <>
              <div className="card p-4">
                <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                  <dt>Id:</dt>
                  <dd>
                  <NavLink
                    to={`/admin/experiences/${experience.companyId}/${experience.id}`}
                    className="underline"
                  >
                    {experience.id}
                  </NavLink>
                  </dd>
                  <dt>Name:</dt>
                  <dd>{experience.name}</dd>
                  <dt>Description:</dt>
                  <dd>{experience.shortDescription}</dd>
                </dl>
                </div>
            </>
          ) : (
            <Ghost additionalClasses="h-24 col-span-2 rounded" />
          )}
        </div>
      </div>
    </section>
  );
}
